import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, SectionLabel } from '../components/ui.js';
import { apiFetch } from '../lib/api.js';
import { LANGUAGES, setLanguage, useT } from '../lib/i18n.js';
import { useProfile } from '../lib/ordering.js';
import { useAuthStore } from '../stores/auth.js';

/**
 * Where your food is left, and how to stop it being left there.
 *
 * The one thing an employee can change about themselves. It matters more than
 * it looks: somebody who moves from Building 1 to Formation keeps collecting
 * lunch at the wrong end of the plant until this screen exists.
 */
export default function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useProfile();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const { t, lang } = useT();

  const setPoint = useMutation({
    mutationFn: (defaultPointId: string) =>
      apiFetch('/api/me/profile', { method: 'PATCH', body: { defaultPointId } }),
    onSuccess: async () => {
      // Both: the pickup point is stamped onto an order when it is placed, and
      // it is also the site filter behind which restaurants are on the menu.
      await queryClient.invalidateQueries({ queryKey: ['me', 'profile'] });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const signOut = useMutation({
    // The refresh cookie is httpOnly, so only the server can drop it. Clearing
    // locally without telling it would leave a ninety-day session alive.
    mutationFn: () => apiFetch('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      clear();
      navigate('/signin', { replace: true });
    },
  });

  const points = profile?.deliveryPoints ?? [];

  return (
    <>
      {/* Same dark head as Today, so the app reads as one thing. */}
      <header className="rounded-b-[34px] border border-line-strong bg-white px-[22px] pt-[calc(26px+env(safe-area-inset-top))] pb-7">
        <b className="block text-[25px] md:text-[30px] tracking-[-0.04em]">
          {user?.name ?? profile?.name ?? 'You'}
        </b>
        <span className="mt-1 block text-[13.5px] text-muted md:text-[15px]">
          {[profile?.departmentName, profile?.siteName].filter(Boolean).join(' · ') || '—'}
        </span>
      </header>

      <div className="px-[22px] pt-5 pb-8 md:px-2 md:pt-7">
        <SectionLabel>{t('Pickup point')}</SectionLabel>
        <p className="mt-2 text-[13.5px] md:text-[15px] text-muted">
          {t('Where your meals are delivered. Changing it affects new orders — ones you have already placed keep the point they were made with.')}
        </p>

        <fieldset className="mt-3.5 flex flex-col gap-2.5">
          <legend className="sr-only">Pickup point</legend>

          {points.length === 0 && (
            <Card className="px-4 py-4 text-[14px] md:text-[15px] text-muted">
              {t('No pickup points are set up for your site yet. Ask your company admin.')}
            </Card>
          )}

          {points.map((point) => {
            const selected = point.id === profile?.defaultPointId;
            return (
              <label
                key={point.id}
                className={`flex cursor-pointer items-center gap-3.5 rounded-control px-4 py-4 ${
                  selected
                    ? 'border-[1.5px] border-brand-bright bg-panel'
                    : 'border-[1.5px] border-transparent bg-panel'
                }`}
              >
                <input
                  type="radio"
                  name="defaultPoint"
                  className="sr-only"
                  checked={selected}
                  disabled={setPoint.isPending}
                  onChange={() => setPoint.mutate(point.id)}
                />
                <span
                  aria-hidden
                  className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? 'border-brand-bright' : 'border-line-dashed'
                  }`}
                >
                  <span
                    className={`size-[11px] rounded-full ${selected ? 'bg-brand-bright' : 'bg-transparent'}`}
                  />
                </span>
                <span className="min-w-0 leading-[1.3]">
                  <b className="block text-[15px] md:text-[16.5px]">{point.name}</b>
                  <span className="text-[13px] md:text-[14.5px] text-muted">{point.note}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        {setPoint.isError && (
          <p className="mt-2.5 rounded-tile bg-warn-soft px-4 py-3 text-[13.5px] md:text-[15px] text-warn-ink">
            {setPoint.error.message}
          </p>
        )}

        <div className="mt-6">
          <SectionLabel>{t('Language')}</SectionLabel>
          {/* Kept on the device rather than on the account: the same person
              reads Korean on their own phone and English on a shared one in
              the office, and neither is wrong. */}
          <div className="mt-2.5 flex flex-wrap gap-2.5">
            {LANGUAGES.map((choice) => (
              <button
                key={choice.code}
                type="button"
                onClick={() => setLanguage(choice.code)}
                className={`h-11 rounded-control px-4 text-[14px] font-extrabold ${
                  lang === choice.code
                    ? 'bg-brand text-white'
                    : 'border border-line-strong bg-white text-ink'
                }`}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <SectionLabel>{t('Alerts')}</SectionLabel>
          <Card className="mt-2.5 px-4 py-4 text-[13.5px] md:text-[15px] text-muted">
            Cutoff reminders, order confirmations and new menus arrive in the app. Email is not
            switched on yet, so there is nothing here to turn off.
          </Card>
        </div>

        <div className="mt-6">
          <SectionLabel>{t('Password')}</SectionLabel>
          <ChangePassword />
        </div>

        <div className="mt-7">
          <Button variant="outline" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
            {signOut.isPending ? `${t('Sign out')}…` : t('Sign out')}
          </Button>
        </div>
      </div>
    </>
  );
}

const FIELD =
  'h-[52px] w-full rounded-control border border-line-strong bg-white px-4 text-[15px] ' +
  'font-bold outline-none focus:border-brand-bright md:text-[16.5px]';

/**
 * Changing a password from inside the app.
 *
 * Without this the only way to change one is to mail yourself a reset link,
 * which is the errand this whole change was made to stop.
 */
function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      setDone(true);
      setOpen(false);
      setCurrent('');
      setNext('');
    },
  });

  if (!open) {
    return (
      <Card className="mt-2.5 px-4 py-4">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setDone(false);
          }}
          className="text-[14.5px] font-extrabold text-brand-ink md:text-[16px]"
        >
          Change your password
        </button>
        {done && (
          <p className="mt-1.5 text-[13.5px] text-ok-ink md:text-[15px]">Changed.</p>
        )}
      </Card>
    );
  }

  return (
    <Card className="mt-2.5 px-4 py-4">
      <form
        className="flex flex-col gap-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
      >
        <input
          type="password"
          autoComplete="current-password"
          placeholder="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={FIELD}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="New password, 10 characters or more"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={FIELD}
        />

        {change.isError && (
          <p className="text-[13.5px] font-bold text-danger md:text-[15px]">
            {(change.error as Error).message}
          </p>
        )}

        <div className="flex gap-2.5">
          <Button
            type="submit"
            disabled={change.isPending || current.length === 0 || next.length < 10}
          >
            {change.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
